import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Pencil, Trash2, Loader2, WifiOff, ShieldCheck, Ban,
  User, Building2, Zap, AlertTriangle,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/shared/EmptyState";
import LoadError from "@/components/dashboard/LoadError";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { listGuardrails, createGuardrail, updateGuardrail, deleteGuardrail } from "@/api/admin";
import { toastError } from "@/lib/toast";

const SCOPES = [
  { value: "per_tester", label: "Per-tester / month", icon: User },
  { value: "workspace", label: "Workspace / month", icon: Building2 },
  { value: "per_run", label: "Per single run", icon: Zap },
];
const scopeMeta = (v) => SCOPES.find((s) => s.value === v) || SCOPES[0];

const EMPTY = { name: "", scope: "per_tester", threshold_usd: 5, hard_stop: false, alert: true, enabled: true };

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center justify-between gap-4 py-2">
      {label && <span className="text-sm text-slate-700 dark:text-slate-200">{label}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn("relative h-6 w-11 shrink-0 rounded-full transition-colors", checked ? "bg-brand-600" : "bg-slate-300 dark:bg-white/15")}
      >
        <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform", checked ? "translate-x-[22px]" : "translate-x-0.5")} />
      </button>
    </label>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className={cn("grid h-10 w-10 place-items-center rounded-xl", accent)}><Icon size={18} /></span>
      <div>
        <p className="font-display text-xl font-bold text-slate-900 dark:text-white">{value}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </div>
  );
}

export default function Guardrails() {
  const { data, loading, demo, error, reload } = useApiData(listGuardrails, []);
  const rules = Array.isArray(data) ? data : [];

  // Which enabled rules already cover a scope. Nothing prevented two rules on
  // one scope and nothing said which applied; the binding rule is the lowest
  // threshold, and a hard stop beats an alert.
  const conflictFor = (scope, excludeId) =>
    rules.filter((g) => g.enabled && g.scope === scope && g.id !== excludeId);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Another enabled rule already covering the scope being edited. Not blocked —
  // overlapping caps are legitimate — but the operator has to be told WHICH one
  // will bind, because nothing on the page said.
  const scopeConflicts = conflictFor(form.scope, editing);

  const enabledCount = rules.filter((g) => g.enabled).length;
  const hardStops = rules.filter((g) => g.enabled && g.hard_stop).length;
  const alerts = rules.filter((g) => g.enabled && g.alert).length;

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(rule) { setEditing(rule.id); setForm({ ...EMPTY, ...rule }); setOpen(true); }

  async function save() {
    // These used to `return` silently, so Save looked like a dead button.
    const name = form.name.trim();
    if (!name) return toastError("Give the guardrail a name.");
    const threshold = Number(form.threshold_usd);
    if (!Number.isFinite(threshold) || threshold < 0) return toastError("Enter a threshold of 0 or more.");
    if (form.hard_stop && threshold <= 0) {
      return toastError("A hard stop needs a threshold above $0, otherwise it would block every run.");
    }
    setBusy(true);
    const payload = {
      name, scope: form.scope, threshold_usd: threshold,
      hard_stop: !!form.hard_stop, alert: !!form.alert, enabled: !!form.enabled,
    };
    try {
      if (editing) await updateGuardrail(editing, payload);
      else await createGuardrail(payload);
    } catch (e) { setBusy(false); toastError(e.message || "Couldn't save the guardrail."); return; }
    setBusy(false); setOpen(false); reload();
  }

  // Per-row busy state, and reload ONLY on success. Rapid clicks used to queue
  // overlapping writes, and a failed update still triggered a reload that
  // repainted the old value — indistinguishable from a click that never landed.
  async function toggleEnabled(g) {
    if (togglingId) return;
    setTogglingId(g.id);
    try {
      await updateGuardrail(g.id, {
        name: g.name, scope: g.scope, threshold_usd: g.threshold_usd,
        hard_stop: g.hard_stop, alert: g.alert, enabled: !g.enabled,
      });
      await reload();
    } catch (e) {
      toastError(e.message || "Couldn't update the guardrail.");
    } finally {
      setTogglingId(null);
    }
  }

  async function confirmDelete() {
    if (!delTarget) return;
    setDelBusy(true);
    try { await deleteGuardrail(delTarget.id); } catch (e) { toastError(e.message || "Couldn't delete the guardrail."); }
    setDelBusy(false); setDelTarget(null); reload();
  }

  return (
    <>
      <PageHeader
        title="Budget Guardrails"
        subtitle="Cap AI spend so bills never surprise you. When two enabled rules cover the same scope, the LOWEST threshold binds, and any hard stop wins over an alert."
        actions={<Button size="sm" onClick={openCreate}><Plus size={16} /> Add rule</Button>}
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Backend not reachable — changes won&apos;t persist.
        </div>
      )}

      {/* stats */}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={ShieldCheck} label="Total rules" value={rules.length} accent="bg-brand-500/10 text-brand-600 dark:text-brand-300" />
        <StatTile icon={ShieldCheck} label="Enabled" value={enabledCount} accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" />
        <StatTile icon={Ban} label="Hard-stops" value={hardStops} accent="bg-rose-500/10 text-rose-600 dark:text-rose-300" />
        <StatTile icon={AlertTriangle} label="Warn at cap" value={alerts} accent="bg-amber-500/10 text-amber-600 dark:text-amber-300" />
      </div>

      <LoadError message={error} onRetry={reload} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : rules.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No guardrails yet" message="Add a spend cap to protect your budget." action={<Button onClick={openCreate}><Plus size={16} /> Add rule</Button>} />
      ) : (
        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {rules.map((g) => {
              const meta = scopeMeta(g.scope);
              return (
                <motion.div
                  key={g.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  className={cn("card flex flex-col gap-4 p-4 transition-opacity sm:flex-row sm:items-center sm:justify-between", !g.enabled && "opacity-60")}
                >
                  <div className="flex items-center gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><meta.icon size={20} /></span>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-semibold text-slate-800 dark:text-white">
                        {g.name}
                        {g.hard_stop && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[0.625rem] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"><Ban size={10} /> hard-stop</span>}
                        {g.alert && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"><AlertTriangle size={10} /> warn at cap</span>}
                      </p>
                      <p className="text-xs text-slate-400">{meta.label}</p>
                      {/* Live spend against the cap. The page used to show the
                          stored config only, so a rule quietly over budget
                          looked identical to one nowhere near it. */}
                      {g.usage_usd != null && (
                        <p className={cn("mt-1 text-xs font-medium", g.breached ? "text-rose-600 dark:text-rose-300" : "text-slate-500 dark:text-slate-400")}>
                          ${Number(g.usage_usd).toFixed(2)} used
                          {g.ratio != null && ` · ${Math.round(g.ratio * 100)}%`}
                          {g.breached && (g.hard_stop ? " · blocking new runs" : " · over budget")}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-3">
                    <div className="text-right">
                      <p className="font-display text-lg font-bold text-slate-900 dark:text-white">${Number(g.threshold_usd).toFixed(2)}</p>
                      <p className="text-[0.6875rem] text-slate-400">cap</p>
                    </div>
                    <div className="h-9 w-px bg-slate-200 dark:bg-white/10" />
                    <Toggle checked={g.enabled} onChange={() => toggleEnabled(g)} />
                    <button onClick={() => openEdit(g)} title="Edit" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-brand-300 hover:text-brand-600 dark:border-white/10"><Pencil size={15} /></button>
                    <button onClick={() => setDelTarget(g)} title="Delete" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-rose-300 hover:text-rose-600 dark:border-white/10"><Trash2 size={15} /></button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* add / edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit guardrail" : "Add guardrail"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || !form.name.trim()}>{busy ? <Loader2 size={15} className="animate-spin" /> : "Save rule"}</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Name" placeholder="e.g. Per-tester monthly cap" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Scope</span>
            <div className="grid grid-cols-1 gap-2 xs:grid-cols-3">
              {SCOPES.map((s) => {
                const active = form.scope === s.value;
                return (
                  <button key={s.value} type="button" onClick={() => setForm((f) => ({ ...f, scope: s.value }))}
                    className={cn("flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-colors", active ? "border-brand-400 bg-brand-50/60 dark:border-brand-500/40 dark:bg-brand-500/10" : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5")}>
                    <s.icon size={18} className={active ? "text-brand-600 dark:text-brand-300" : "text-slate-400"} />
                    <span className="text-[0.6875rem] font-medium leading-tight text-slate-700 dark:text-slate-200">{s.label}</span>
                  </button>
                );
              })}
            </div>
            {/* Overlapping caps are legitimate, so this warns rather than
                blocks — but which rule actually binds was previously not stated
                anywhere on the page. */}
            {scopeConflicts.length > 0 && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>
                  {scopeConflicts.length === 1 ? "Another enabled rule" : `${scopeConflicts.length} other enabled rules`}
                  {" "}already {scopeConflicts.length === 1 ? "covers" : "cover"} this scope
                  {" "}({scopeConflicts.map((g) => `${g.name} $${g.threshold_usd}`).join(", ")}).
                  The lowest threshold binds, and any hard stop wins over an alert.
                </span>
              </p>
            )}
          </div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Threshold (USD)</span>
            <span className="relative block">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input type="number" step="0.01" value={form.threshold_usd} onChange={(e) => setForm((f) => ({ ...f, threshold_usd: e.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-7 pr-4 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white" />
            </span>
          </label>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 px-3 dark:divide-white/5 dark:border-white/10">
            {/* Named for what the backend does: guardrails.enforce() writes a
                server WARNING once usage reaches the threshold. Nothing mails
                anyone, and there is no pre-breach trigger, so the old "Email
                alert near the cap" promised two features that don't exist. */}
            <Toggle checked={form.alert} onChange={(v) => setForm((f) => ({ ...f, alert: v }))} label="Log a warning when the cap is reached" />
            <Toggle checked={form.hard_stop} onChange={(v) => setForm((f) => ({ ...f, hard_stop: v }))} label="Hard-stop runs at the cap" />
            <Toggle checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} label="Enabled" />
          </div>
        </div>
      </Modal>

      {/* delete confirm */}
      <Modal
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        title="Delete guardrail?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDelTarget(null)}>Cancel</Button>
            <button type="button" disabled={delBusy} onClick={confirmDelete} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-60">
              {delBusy ? <Loader2 size={15} className="animate-spin" /> : <><Trash2 size={15} /> Delete</>}
            </button>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-300"><AlertTriangle size={18} /></span>
          <p className="text-sm text-slate-600 dark:text-slate-300">Remove <strong>{delTarget?.name}</strong>? Spend will no longer be capped by this rule. This can&apos;t be undone.</p>
        </div>
      </Modal>
    </>
  );
}
